import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { useMediaQuery } from "@/hooks/use-mobile";
import type { Resident } from "@shared/schema";
import { CommunityCard } from "@/components/community-card";

interface CommunitySectionProps {
  residents?: Resident[];
  isLoading: boolean;
}

export function CommunitySection({ residents, isLoading }: CommunitySectionProps) {
  const [api, setApi] = useState<any>()
  const isMobile = useMediaQuery("(max-width: 768px)")
  
  // Auto-play functionality
  useEffect(() => {
    if (!api) return

    const interval = setInterval(() => {
      api.scrollNext()
    }, 3000)

    return () => clearInterval(interval)
  }, [api])

  if (isLoading) {
    return (
      <section className="py-12" data-testid="section-community">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-64 mx-auto mb-2 rounded-xl neo-card-sm" />
          <Skeleton className="h-4 w-96 mx-auto rounded-xl neo-card-sm" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-6 rounded-2xl bg-card neo-card">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <Skeleton className="w-20 h-20 md:w-24 md:h-24 rounded-full flex-shrink-0 neo-avatar" />
                <div className="flex-1 text-center md:text-left">
                  <Skeleton className="h-6 w-32 mb-2 rounded-xl neo-card-sm mx-auto md:mx-0" />
                  <Skeleton className="h-5 w-24 mb-3 rounded-xl neo-card-sm mx-auto md:mx-0" />
                  <Skeleton className="h-4 w-full max-w-[200px] rounded-xl neo-card-sm mx-auto md:mx-0" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!residents || residents.length === 0) {
    return null;
  }

  return (
    <section className="py-12" data-testid="section-community">
      <div className="text-center mb-8">
        <h2 className="font-poppins text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="heading-community">
          Our Community
        </h2>
        <p className="font-inter text-muted-foreground text-lg" data-testid="text-community-subtitle">
          Meet the dedicated members serving our village
        </p>
      </div>

      <div className="relative max-w-7xl mx-auto py-8">
        <Carousel
          setApi={setApi}
          className="w-full"
          opts={{
            align: "start",
            loop: true,
          }}
        >
          <CarouselContent className="!ml-0 gap-6 py-4">
            {residents.map((resident) => (
              <CarouselItem 
                key={resident.id} 
                className="!pl-0 basis-full md:basis-1/2 lg:basis-1/3"
              >
                <CommunityCard resident={resident} />
              </CarouselItem>
            ))}
          </CarouselContent>
          
          {/* Custom arrow buttons at the bottom */}
          <div className="flex justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-12 w-12 shadow-md hover:shadow-lg transition-shadow"
              onClick={() => api?.scrollPrev()}
              data-testid="carousel-prev-button"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-12 w-12 shadow-md hover:shadow-lg transition-shadow"
              onClick={() => api?.scrollNext()}
              data-testid="carousel-next-button"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>
        </Carousel>
      </div>
    </section>
  );
}